import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useWebsiteContentApi } from "./useWebsiteContentApi";

const queryKeys = {
    component: (propertyId, componentId) => ['websiteContent', 'component', { propertyId, componentId }],
    all: (propertyId) => ['websiteContent', 'all', { propertyId }],
};

/**
 * Fetches website content for a property.
 *
 * - Pass componentId to fetch a single component (findById).
 * - Omit componentId to fetch all components (findAll).
 *
 * When fetching a single component, the findAll cache is checked first.
 * If the component is already there, it is used as initialData so no
 * extra network request is made.
 */
export const useWebsiteContentData = (propertyId, componentId) => {
    const { findById, findAll, createComponent, updateComponent, deleteComponent, uploadImages, deleteImages } = useWebsiteContentApi();
    const queryClient = useQueryClient();

    const isSingle = !!componentId;

    const queryResults = useQuery({
        queryKey: isSingle
            ? queryKeys.component(propertyId, componentId)
            : queryKeys.all(propertyId),
        queryFn: () => isSingle
            ? findById(propertyId, componentId)
            : findAll(propertyId),
        enabled: !!propertyId,
        staleTime: defaultStaleTime,
        initialData: isSingle
            ? () => {
                const allComponents = queryClient.getQueryData(queryKeys.all(propertyId));
                return allComponents?.find((c) => c.id === componentId);
            }
            : undefined,
    });

    const getCached = () => queryClient.getQueryData(queryKeys.all(propertyId));

    const patchCache = (updatedComponent) => {
        const cached = getCached();
        if (cached) {
            queryClient.setQueryData(
                queryKeys.all(propertyId),
                cached.map((c) => (c.id === updatedComponent.id ? updatedComponent : c))
            );
        }
    };

    const _createComponent = async (component) => {
        const created = await createComponent(propertyId, component);
        const cached = getCached();
        if (cached) {
            queryClient.setQueryData(queryKeys.all(propertyId), [...cached, created]);
        }
        return created;
    };

    const _updateComponent = async (id, component) => {
        const updated = await updateComponent(propertyId, id, component);
        patchCache(updated);
        return updated;
    };

    const _deleteComponent = async (id) => {
        await deleteComponent(propertyId, id);
        const cached = getCached();
        if (cached) {
            queryClient.setQueryData(
                queryKeys.all(propertyId),
                cached.filter((c) => c.id !== id)
            );
        }
    };

    const _uploadImages = async (id, files) => {
        const updated = await uploadImages(propertyId, id, files);
        if (updated) patchCache(updated);
        return updated;
    };

    const _deleteImages = async (id, fileNames) => {
        const updated = await deleteImages(propertyId, id, fileNames);
        if (updated) patchCache(updated);
        return updated;
    };

    return {
        queryResults,
        createComponent: _createComponent,
        updateComponent: _updateComponent,
        deleteComponent: _deleteComponent,
        uploadImages: _uploadImages,
        deleteImages: _deleteImages,
    };
};
